import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Teleport as _Teleport, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text" }

import { PropType, ref, computed, watch } from 'vue'
import Arrow from '@/design-system/atoms/TooltipArrow.vue'

type BoundingRect = {
  x: number
  y: number
  width: number
  height: number
  top: number
  right: number
  bottom: number
  left: number
}
type Positioning =
  | 'bottom-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'top-left'
  | 'top-right'
  | 'center-left'
  | 'center-right'

const arrowHeight = 10
const arrowWidth = 20


export default /*@__PURE__*/_defineComponent({
  __name: 'TooltipStandardV2',
  props: {
  // eslint-disable-next-line vue/require-default-prop
  text: {
    type: String,
    required: false
  },
  teleportTo: {
    type: String,
    required: false,
    default: '#app'
  },
  arrowPosition: {
    type: String as PropType<Positioning>,
    required: false,
    default: 'bottom-right'
  },
  horizontalOffset: {
    type: Number,
    required: false,
    default: 0
  },
  verticalOffset: {
    type: Number,
    required: false,
    default: 0
  },
  tooltipOffset: {
    type: Number,
    required: false,
    default: 0
  },
  heatmap: {
    type: Boolean,
    required: false,
    default: false
  },
  disabledTooltip: {
    type: Boolean,
    required: false,
    default: false
  }
},
  setup(__props) {

_useCssVars(_ctx => ({
  "2f510988": (opacity.value)
}))

const props = __props

const container = ref<HTMLElement | undefined>()
const tooltip = ref<HTMLElement | undefined>()
const hovered = ref(false)
const tooltipStyle = ref<any>({})
const arrowStyle = ref<any>({})
const opacity = computed(() => {
  return hovered.value ? 1 : 0
})
const isTextTooLong = computed(() => {
  // Define a threshold for the maximum length of text that should trigger the tooltip
  const maxTextLength = 17 // You can change this value to your desired length

  return props.text && props.text.length > maxTextLength
})

watch(hovered, (isHovered, oldValue) => {
  if (isHovered != oldValue && props.text) {
    isHovered ? showTooltip() : hideTooltip()
  }
})

function hideTooltip() {
  tooltipStyle.value = {
    visibility: 'hidden',
    opacity: 0
  }
  arrowStyle.value = {
    visibility: 'hidden',
    opacity: 0
  }
}

function showTooltip() {
  if (!container.value || !tooltip.value) {
    return
  }
  const containerRect: BoundingRect = container.value.getBoundingClientRect()
  const tooltipRect: BoundingRect = tooltip.value.getBoundingClientRect()
  arrowStyle.value = {
    visibility: 'visible',
    opacity: 1,
    ...makeArrowStyle(containerRect)
  }
  tooltipStyle.value = {
    visibility: 'visible',
    opacity: 1,
    ...makeTooltipStyle(containerRect, tooltipRect)
  }
}

function makeArrowStyle(containerRect: BoundingRect) {
  switch (props.arrowPosition) {
    case 'top-left':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + props.horizontalOffset}px`,
        'transform-origin': 'top left',
        transform: `translateY(${arrowHeight}px) translateX(${arrowWidth}px) rotate(180deg)`
      }
    case 'top-center':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + props.horizontalOffset}px`,
        'transform-origin': 'top left',
        transform: `translateY(${arrowHeight}px) translateX(${
          arrowWidth + containerRect.width / 2 - arrowWidth / 2
        }px) rotate(180deg)`
      }
    case 'top-right':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width * 1.45 - props.horizontalOffset}px`,
        'transform-origin': 'top left',
        transform: `translateY(${arrowHeight}px) translateX(-${arrowWidth}px) rotate(180deg)`
      }
    case 'bottom-left':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left - props.horizontalOffset}px`,
        transform: `translateY(-${arrowHeight}px)`
      }
    case 'bottom-center':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left + props.horizontalOffset}px`,
        transform: `translateY(-${arrowHeight}px) translateX(${
          containerRect.width / 2 - arrowWidth / 2
        }px)`
      }
    case 'bottom-right':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width - props.horizontalOffset}px`,
        transform: `translateY(-${arrowHeight}px) translateX(-${arrowWidth}px)`
      }
    case 'center-left':
      return {
        top: `${containerRect.top + props.verticalOffset + containerRect.height}px`,
        left: containerRect.left + props.horizontalOffset,
        'transform-origin': 'top left',
        transform: `translateY(-${
          containerRect.height / 2 - arrowWidth / 2
        }px) translateX(-${arrowHeight}px) rotate(270deg)`
      }
    case 'center-right':
      return {
        top: `${containerRect.top + props.verticalOffset + containerRect.height}px`,
        left: containerRect.left + containerRect.width + props.horizontalOffset,
        'transform-origin': 'top left',
        transform: `translateY(-${arrowWidth / 2 + containerRect.height / 2}px) translateX(${
          arrowWidth / 2
        }px) rotate(90deg)`
      }
    default:
      throw new Error('Fatal: Unsupported arrow position')
  }
}

function makeTooltipStyle(containerRect: BoundingRect, tooltipRect: BoundingRect) {
  switch (props.arrowPosition) {
    case 'bottom-left':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left - 6}px`,
        transform: `translateY(-${tooltipRect.height + arrowHeight}px) translateX(-${
          props.tooltipOffset
        }px)`
      }
    case 'bottom-center':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width / 2}px`,
        transform: `translateY(-${tooltipRect.height + arrowHeight}px) translateX(-50%)`
      }
    case 'bottom-right':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width + 6}px`,
        transform: `translateY(-${tooltipRect.height + arrowHeight}px) translateX(${
          props.tooltipOffset - tooltipRect.width + props.horizontalOffset
        }px)`
      }
    case 'top-left':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left}px`,
        transform: `translateY(${arrowHeight}px) translateX(${props.tooltipOffset}px)`
      }
    case 'top-center':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width + props.horizontalOffset}px`,
        transform: `translateY(${arrowHeight}px) translateX(-${
          tooltipRect.width / 2 + containerRect.width / 2 + props.tooltipOffset
        }px)`
      }
    case 'top-right':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width * 1.2}px`,
        transform: `translateY(${arrowHeight}px) translateX(-${
          tooltipRect.width + props.tooltipOffset - props.horizontalOffset
        }px)`
      }
    case 'center-right':
      return {
        top: `${containerRect.top + (props.verticalOffset + containerRect.height)}px`,
        left: `${
          containerRect.left + (containerRect.width + arrowHeight + props.horizontalOffset)
        }px`,
        transform: `translateY(-${tooltipRect.height / 2 + containerRect.height / 2}px)`
      }
    case 'center-left':
      return {
        top: `${containerRect.top + props.verticalOffset}px`,
        left: `${
          containerRect.left - (tooltipRect.width + arrowHeight + props.horizontalOffset)
        }px`,
        transform: `translateY(-${tooltipRect.height / 2 - containerRect.height / 2}px)`
      }
    default:
      throw new Error('Fatal: Unsupported arrow position')
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "container",
    ref: container,
    class: "tooltip-container",
    onMouseenter: _cache[0] || (_cache[0] = ($event: any) => (hovered.value = true)),
    onMouseleave: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (hovered.value = false), ["stop"]))
  }, [
    _renderSlot(_ctx.$slots, "default"),
    (_openBlock(), _createBlock(_Teleport, { to: __props.teleportTo }, [
      (!__props.disabledTooltip || (isTextTooLong.value && __props.heatmap))
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "tooltip",
            ref: tooltip,
            class: "tooltip",
            style: _normalizeStyle(tooltipStyle.value)
          }, [
            _createElementVNode("span", _hoisted_1, _toDisplayString(__props.text), 1)
          ], 4))
        : _createCommentVNode("", true),
      (!__props.disabledTooltip || (isTextTooLong.value && __props.heatmap))
        ? (_openBlock(), _createBlock(Arrow, {
            key: 1,
            class: "arrow",
            style: _normalizeStyle(arrowStyle.value)
          }, null, 8, ["style"]))
        : _createCommentVNode("", true)
    ], 8, ["to"]))
  ], 544))
}
}

})